import React, { useState, useEffect } from 'react';
import { 
  XMarkIcon, 
  UserPlusIcon, 
  UserMinusIcon,
  MagnifyingGlassIcon,
  CheckIcon,
  XCircleIcon
} from '@heroicons/react/24/outline';
import useCourseUsers from '../hooks/useCourseUsers';
import useBookAssignments from '../hooks/useBookAssignments';

const UserAssignmentModal = ({ isOpen, onClose, book }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [showAssignedUsers, setShowAssignedUsers] = useState(false);
  
  const { users, students, loading: usersLoading, error: usersError } = useCourseUsers();
  const { 
    assignedUsers, 
    loading: assignmentsLoading, 
    operationLoading,
    assignUsers, 
    removeUsers,
    refreshAssignedUsers 
  } = useBookAssignments(book?.id);

  // Filter users based on search term
  const filteredUsers = users.filter(user => {
    if (!searchTerm) return true;
    return (
      user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.telephone?.includes(searchTerm)
    );
  });

  // Filter out already assigned users
  const availableUsers = filteredUsers.filter(user => 
    !assignedUsers.some(assignment => assignment.userId === user.id)
  );

  const handleUserToggle = (userId) => {
    setSelectedUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleAssignUsers = async () => {
    if (selectedUsers.length === 0) return;
    
    const result = await assignUsers(selectedUsers);
    if (result.success) {
      setSelectedUsers([]);
      setSearchTerm('');
    }
  };

  const handleUnassignUser = async (userId) => {
    await removeUsers([userId]);
  };

  const handleUnassignMultiple = async (userIds) => {
    await removeUsers(userIds);
  };

  const handleClose = () => {
    setSelectedUsers([]);
    setSearchTerm('');
    setShowAssignedUsers(false);
    onClose();
  };

  if (!isOpen || !book) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              Manage Users for "{book.title}"
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              Assign or remove users from this book
            </p>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b">
          <button
            onClick={() => setShowAssignedUsers(false)}
            className={`px-6 py-3 text-sm font-medium transition-colors ${
              !showAssignedUsers
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Assign Users ({availableUsers.length})
          </button>
          <button
            onClick={() => setShowAssignedUsers(true)}
            className={`px-6 py-3 text-sm font-medium transition-colors ${
              showAssignedUsers
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Assigned Users ({assignedUsers.length})
          </button>
        </div>

        <div className="p-6 overflow-y-auto max-h-[60vh]">
          {showAssignedUsers ? (
            /* Assigned Users Tab */
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  Currently Assigned Users
                </h3>
                {assignedUsers.length > 0 && (
                  <button
                    onClick={() => handleUnassignMultiple(assignedUsers.map(u => u.userId))}
                    disabled={operationLoading}
                    className="inline-flex items-center px-3 py-2 border border-red-300 shadow-sm text-sm leading-4 font-medium rounded-md text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
                  >
                    <UserMinusIcon className="h-4 w-4 mr-2" />
                    Remove All
                  </button>
                )}
              </div>

              {assignmentsLoading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                  <p className="text-gray-500 mt-2">Loading assigned users...</p>
                </div>
              ) : assignedUsers.length === 0 ? (
                <div className="text-center py-8">
                  <UserPlusIcon className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                  <p className="text-gray-500">No users are currently assigned to this book.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {assignedUsers.map((assignment) => (
                    <div
                      key={assignment.userId}
                      className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
                    >
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                          <span className="text-blue-600 font-medium text-sm">
                            {assignment.userData.name?.charAt(0) || 'U'}
                          </span>
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">
                            {assignment.userData.name || 'Unknown User'}
                          </p>
                          <p className="text-sm text-gray-600">
                            {assignment.userData.email}
                          </p>
                          {assignment.assignmentData.timestamp && (
                            <div className="text-xs text-gray-500">
                              Assigned: {new Date(assignment.assignmentData.timestamp.seconds * 1000).toLocaleDateString()}
                            </div>
                          )}
                        </div>
                      </div>
                      <button
                        onClick={() => handleUnassignUser(assignment.userId)}
                        disabled={operationLoading}
                        className="text-red-600 hover:text-red-800 transition-colors disabled:opacity-50"
                        title="Remove user from book"
                      >
                        <XCircleIcon className="h-5 w-5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            /* Assign Users Tab */
            <div>
              <div className="mb-4">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="flex-1">
                    <div className="relative">
                      <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                      <input
                        type="text"
                        placeholder="Search users by name, email, or phone..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </div>
                  {selectedUsers.length > 0 && (
                    <button
                      onClick={handleAssignUsers}
                      disabled={operationLoading}
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                    >
                      <UserPlusIcon className="h-4 w-4 mr-2" />
                      Assign {selectedUsers.length} User{selectedUsers.length !== 1 ? 's' : ''}
                    </button>
                  )}
                </div>

                {selectedUsers.length > 0 && (
                  <div className="mb-4">
                    <p className="text-sm text-gray-600 mb-2">
                      Selected users ({selectedUsers.length}):
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {selectedUsers.map(userId => {
                        const user = users.find(u => u.id === userId);
                        return (
                          <span
                            key={userId}
                            className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                          >
                            {user?.name || 'Unknown User'}
                            <button
                              onClick={() => handleUserToggle(userId)}
                              className="ml-1 text-blue-600 hover:text-blue-800"
                            >
                              <XMarkIcon className="h-3 w-3" />
                            </button>
                          </span>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>

              {usersLoading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                  <p className="text-gray-500 mt-2">Loading users...</p>
                </div>
              ) : usersError ? (
                <div className="text-center py-8">
                  <p className="text-red-500">Error loading users: {usersError}</p>
                </div>
              ) : availableUsers.length === 0 ? (
                <div className="text-center py-8">
                  {searchTerm ? (
                    <>
                      <MagnifyingGlassIcon className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                      <p className="text-gray-500">No users found matching "{searchTerm}"</p>
                    </>
                  ) : (
                    <>
                      <CheckIcon className="h-12 w-12 text-green-400 mx-auto mb-3" />
                      <p className="text-gray-500">All users are already assigned to this book!</p>
                    </>
                  )}
                </div>
              ) : (
                <div className="space-y-2">
                  {availableUsers.map((user) => (
                    <div
                      key={user.id}
                      className={`flex items-center p-3 rounded-lg border cursor-pointer transition-colors ${
                        selectedUsers.includes(user.id)
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                      }`}
                      onClick={() => handleUserToggle(user.id)}
                    >
                      <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center mr-3">
                        <span className="text-gray-600 font-medium text-sm">
                          {user.name?.charAt(0) || 'U'}
                        </span>
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">
                          {user.name || 'Unknown User'}
                        </p>
                        <p className="text-sm text-gray-600">
                          {user.email}
                        </p>
                        {user.telephone && (
                          <p className="text-xs text-gray-500">
                            {user.telephone}
                          </p>
                        )}
                      </div>
                      <div className="ml-3">
                        {selectedUsers.includes(user.id) ? (
                          <CheckIcon className="h-5 w-5 text-blue-600" />
                        ) : (
                          <div className="w-5 h-5 border-2 border-gray-300 rounded"></div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t bg-gray-50">
          <button
            onClick={handleClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default UserAssignmentModal;
